import { Pipe, PipeTransform } from '@angular/core';

// Values whose absolute magnitude is below this threshold are treated as zero.
// Real medication quantities are never this small (1e-10 mg is 100x smaller than
// a picogram), so any value below it is IEEE 754 arithmetic noise from operations
// that mathematically should have produced zero (e.g. 0.1 + 0.2 - 0.3).
const NEAR_ZERO_THRESHOLD = 1e-10;

// Significant digits retained when trimming IEEE 754 precision artifacts.
// Real medication quantities never carry more than 3-4 significant decimal digits;
// 10 provides ample headroom while eliminating the ~15-17 digit doubles' noise tail.
const SIGNIFICANT_DIGITS = 10;

/**
 * Formats a quantity for display following medication-safe decimal rules:
 * - Removes insignificant trailing zeros (1.00 → 1, 1.50 → 1.5)
 * - Removes decimal point for whole numbers (5.0 → 5)
 * - Adds leading zero for values between -1 and 1 (.5 → 0.5)
 * - Preserves all meaningful decimal precision
 * - Strips IEEE 754 floating-point noise (0.40000000000000036 → 0.4)
 * - Returns empty string for null/undefined/non-numeric values
 */
export function formatQuantity(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const parsed = typeof value === 'number' ? value : parseFloat(value);

  if (isNaN(parsed) || !isFinite(parsed)) {
    return '';
  }

  // Collapse "effectively zero" values (e.g. 1.11e-16 from floating-point arithmetic)
  if (Math.abs(parsed) < NEAR_ZERO_THRESHOLD) {
    return '0';
  }

  // Trim trailing IEEE 754 noise by rounding to a safe number of significant digits
  const cleaned = parseFloat(parsed.toPrecision(SIGNIFICANT_DIGITS));

  if (Object.is(cleaned, -0)) {
    return '0';
  }

  return cleaned.toString();
}

@Pipe({
  name: 'formatQuantity',
  pure: true,
  standalone: false
})
export class FormatQuantityPipe implements PipeTransform {
  transform(value: string | number | null | undefined): string {
    return formatQuantity(value);
  }
}
